import React, { useMemo } from 'react';

export default function GlowTrendWidget({ entries }) {
  const last7 = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 6);
    cutoff.setHours(0, 0, 0, 0);
    return [...(entries || [])]
      .filter(e => new Date(e.date + 'T00:00:00') >= cutoff)
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [entries]);

  if (last7.length < 2) return null;

  const scores = last7.map(e => e.glowScore);
  const latest = scores[scores.length - 1];
  const trend = scores[scores.length - 1] - scores[0];
  const avg7 = (scores.reduce((s, v) => s + v, 0) / scores.length).toFixed(1);

  const W = 100, H = 44;
  const maxX = Math.max(1, scores.length - 1);
  const getX = (i) => 4 + (i / maxX) * (W - 8);
  const getY = (s) => H - 5 - ((s - 1) / 9) * (H - 10);
  const pathD = scores.map((s, i) => `${i === 0 ? 'M' : 'L'} ${getX(i).toFixed(1)},${getY(s).toFixed(1)}`).join(' ');

  const trendColor = trend > 0 ? '#10b981' : trend < 0 ? '#f43f5e' : '#a8a29e';
  const trendLabel = trend > 0 ? `↑ +${trend}` : trend < 0 ? `↓ ${trend}` : '→ Stable';

  return (
    <div className="bg-white rounded-3xl border border-stone-100 shadow-sm px-5 py-4 flex items-center gap-4">
      {/* Today's score */}
      <div className="flex-shrink-0 text-center" style={{ width: 52 }}>
        <p className="font-serif text-4xl text-stone-900 leading-none">{latest}</p>
        <p className="font-sans text-xs text-stone-400 mt-1">today</p>
      </div>

      {/* Sparkline */}
      <div className="flex-1 min-w-0">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 44 }}>
          <defs>
            <linearGradient id="sparkGrad7" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#a78bfa" />
              <stop offset="100%" stopColor="#34d399" />
            </linearGradient>
          </defs>
          <path d={pathD} fill="none" stroke="url(#sparkGrad7)" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round" />
          {scores.map((s, i) => (
            <circle key={i} cx={getX(i)} cy={getY(s)}
              r={i === scores.length - 1 ? 4.5 : 3}
              fill={i === scores.length - 1 ? '#7c3aed' : '#fff'}
              stroke={i === scores.length - 1 ? '#7c3aed' : '#d1d5db'}
              strokeWidth="1.5" />
          ))}
        </svg>
        <p className="font-sans text-xs text-stone-400 text-center -mt-1">
          {last7.length} day{last7.length !== 1 ? 's' : ''} · avg {avg7}/10
        </p>
      </div>

      {/* Trend */}
      <div className="flex-shrink-0 text-right" style={{ width: 60 }}>
        <p className="font-sans text-sm font-bold leading-none" style={{ color: trendColor }}>
          {trendLabel}
        </p>
        <p className="font-sans text-xs text-stone-400 mt-1">7d trend</p>
      </div>
    </div>
  );
}
